import { useEffect, useState } from "react";
import { BusyLabel } from "@/components/loading";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, RefreshCw, AlertTriangle, CheckCircle2, FileSearch } from "lucide-react";

/**
 * AdminSearchFieldsAudit
 * -----------------------
 * Panel super_admin (Feb 2026 v11) — diagnóstico del buscador del catálogo.
 * Llama `GET /api/admin/mirror/search-fields-audit` con la sesión autenticada
 * y muestra en pantalla los conteos + muestras + lookup puntual.
 *
 * Read-only. NO corre el reindex, NO muta nada. Solo lectura.
 */

// Formatea números con separador de miles (es-EC)
const fmt = (n) => (typeof n === "number" ? n.toLocaleString("es-EC") : "—");

// Cuadro de métrica grande — usa color según severidad
function Metric({ label, value, color = "neutral", testid }) {
  const map = {
    neutral: "border-[#262626] text-neutral-100",
    good: "border-emerald-800/40 text-emerald-300",
    warn: "border-amber-800/40 text-amber-300",
    bad: "border-red-800/40 text-red-300",
  };
  return (
    <div
      data-testid={testid}
      className={`border rounded-lg p-4 bg-[#0A0A0A] ${map[color]}`}
    >
      <div className="text-xs uppercase tracking-wide text-neutral-400 mb-1">{label}</div>
      <div className="text-3xl font-display leading-none">{fmt(value)}</div>
    </div>
  );
}

export default function AdminSearchFieldsAudit() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Lookup puntual
  const [lookupType, setLookupType] = useState("local_id");
  const [lookupValue, setLookupValue] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupData, setLookupData] = useState(null);

  const runAudit = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/mirror/search-fields-audit");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al correr la auditoría");
    } finally {
      setLoading(false);
    }
  };

  const runLookup = async () => {
    const v = (lookupValue || "").trim();
    if (!v) {
      toast.error("Ingresa un valor para buscar");
      return;
    }
    setLookupLoading(true);
    setLookupData(null);
    try {
      const params = { [`filter_${lookupType}`]: v };
      const r = await api.get("/admin/mirror/search-fields-audit", { params });
      setLookupData(r.data.lookup_docs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al buscar la carta");
    } finally {
      setLookupLoading(false);
    }
  };

  // Corre la auditoría al abrir el panel — un solo request.
  useEffect(() => { runAudit(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const inconsistentes = data?.inconsistentes_total ?? 0;
  const desglose = data?.inconsistentes_desglose || {};
  const muestras = data?.muestras_inconsistentes || [];

  return (
    <section className="space-y-6" data-testid="admin-search-audit">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-display flex items-center gap-2">
            <FileSearch className="w-6 h-6" />
            Auditoría del buscador
          </h1>
          <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
            Diagnóstico read-only del catálogo. Detecta cartas que existen en el
            mirror pero <strong>no aparecen al buscar por nombre</strong> — porque
            su <code className="text-emerald-400">search_norm</code> no incluye
            alguna palabra del nombre real (bug "Mina 211-214"). Cero mutación,
            cero red externa, cero interacción con dinero.
          </p>
        </div>
        <Button
          onClick={runAudit}
          disabled={loading}
          data-testid="admin-search-audit-refresh-btn"
        >
          <BusyLabel busy={loading} text="Cargando…"><RefreshCw className="w-4 h-4 mr-2" />Recargar auditoría</BusyLabel>
        </Button>
      </div>

      {!data && loading && (
        <div className="text-sm text-neutral-400">Corriendo auditoría sobre el mirror...</div>
      )}

      {data && (
        <>
          {/* Fila 1 — panorama del mirror */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-3">Panorama del mirror</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Total cartas" value={data.total_mirror} testid="metric-total-mirror" />
              <Metric label="name_es distinto de name_en" value={data.name_es_diferente_a_name_en} testid="metric-diff-lang" />
              <Metric label="name_es vacío" value={data.name_es_vacio} color={data.name_es_vacio > 0 ? "warn" : "neutral"} testid="metric-es-vacio" />
              <Metric label="name_en vacío" value={data.name_en_vacio} color={data.name_en_vacio > 0 ? "warn" : "neutral"} testid="metric-en-vacio" />
            </div>
          </div>

          {/* Fila 2 — huecos de escritura (Cambio 3 cubrirá futuros) */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-3">Campos de búsqueda faltantes</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="sin search_norm" value={data.sin_search_norm} color={data.sin_search_norm > 0 ? "bad" : "good"} testid="metric-sin-norm" />
              <Metric label="search_norm = ''" value={data.search_norm_vacio_string} color={data.search_norm_vacio_string > 0 ? "bad" : "good"} testid="metric-norm-vacio" />
              <Metric label="sin trigrams" value={data.sin_trigrams} color={data.sin_trigrams > 0 ? "bad" : "good"} testid="metric-sin-trigrams" />
              <Metric label="sin search_prefix" value={data.sin_search_prefix} color={data.sin_search_prefix > 0 ? "bad" : "good"} testid="metric-sin-prefix" />
            </div>
          </div>

          {/* Fila 3 — inconsistentes (el bug real de "Mina") */}
          <div>
            <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-3 flex items-center gap-2">
              {inconsistentes > 0 ? <AlertTriangle className="w-4 h-4 text-red-400" /> : <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              Inconsistentes — nombre real no está en search_norm
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric
                label="Total inconsistentes"
                value={inconsistentes}
                color={inconsistentes > 0 ? "bad" : "good"}
                testid="metric-inconsistentes-total"
              />
              <Metric
                label="Falta palabra ES solo"
                value={desglose.falta_palabra_es_solo}
                color={desglose.falta_palabra_es_solo > 0 ? "warn" : "neutral"}
                testid="metric-falta-es"
              />
              <Metric
                label="Falta palabra EN solo"
                value={desglose.falta_palabra_en_solo}
                color={desglose.falta_palabra_en_solo > 0 ? "warn" : "neutral"}
                testid="metric-falta-en"
              />
              <Metric
                label="Falta en ambos idiomas"
                value={desglose.falta_ambos_idiomas}
                color={desglose.falta_ambos_idiomas > 0 ? "bad" : "neutral"}
                testid="metric-falta-ambos"
              />
            </div>

            {data.inconsistentes_top_sets?.length > 0 && (
              <div className="mt-4 border border-[#262626] rounded-lg p-4 bg-[#0A0A0A]">
                <div className="text-sm text-neutral-400 mb-2">Sets con más inconsistencias (top 10):</div>
                <div className="flex flex-wrap gap-2">
                  {data.inconsistentes_top_sets.map((s, i) => (
                    <span key={i} className="text-xs bg-[#171717] border border-[#2a2a2a] rounded px-2 py-1">
                      {s.set_name} <span className="text-red-400 font-bold ml-1">{s.count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tabla de muestras */}
          {muestras.length > 0 && (
            <div>
              <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-3">
                Primeras {muestras.length} muestras inconsistentes
              </h2>
              <div className="border border-[#262626] rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="muestras-tabla">
                    <thead className="bg-[#0A0A0A] text-xs uppercase text-neutral-400">
                      <tr>
                        <th className="text-left px-3 py-2">ID</th>
                        <th className="text-left px-3 py-2">local_id</th>
                        <th className="text-left px-3 py-2">name_es</th>
                        <th className="text-left px-3 py-2">name_en</th>
                        <th className="text-left px-3 py-2">Categoría</th>
                        <th className="text-left px-3 py-2">Palabras faltantes</th>
                        <th className="text-left px-3 py-2">Set</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#171717]">
                      {muestras.map((m, i) => (
                        <tr key={i} data-testid={`muestra-row-${i}`}>
                          <td className="px-3 py-2 font-mono text-xs">{m.id}</td>
                          <td className="px-3 py-2">{m.local_id || "—"}</td>
                          <td className="px-3 py-2">{m.name_es || <span className="text-neutral-500 italic">vacío</span>}</td>
                          <td className="px-3 py-2">{m.name_en || <span className="text-neutral-500 italic">vacío</span>}</td>
                          <td className="px-3 py-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              m.categoria === "ambos" ? "bg-red-900/30 text-red-300" :
                              m.categoria === "solo_en" ? "bg-amber-900/30 text-amber-300" :
                              "bg-blue-900/30 text-blue-300"
                            }`}>
                              {m.categoria}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {m.palabras_faltantes_es?.length > 0 && <span className="text-blue-300">ES: {m.palabras_faltantes_es.join(", ")}</span>}
                            {m.palabras_faltantes_es?.length > 0 && m.palabras_faltantes_en?.length > 0 && <br />}
                            {m.palabras_faltantes_en?.length > 0 && <span className="text-amber-300">EN: {m.palabras_faltantes_en.join(", ")}</span>}
                          </td>
                          <td className="px-3 py-2 text-neutral-400 text-xs">{m.set_name || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Sección de lookup puntual */}
      <div className="border border-[#262626] rounded-lg p-6 bg-[#0A0A0A] space-y-4">
        <div>
          <h2 className="text-lg font-display flex items-center gap-2">
            <Search className="w-5 h-5" />
            Buscar carta específica
          </h2>
          <p className="text-sm text-neutral-400 mt-1">
            Ingresa un <code>local_id</code> (ej. <code>211</code> para "Mina"),
            un fragmento de nombre, o parte del id — para ver EXACTAMENTE cómo
            está guardado el doc en el mirror.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="w-full md:w-56">
            <Label className="text-xs text-neutral-400 mb-1 block">Buscar por</Label>
            <Select value={lookupType} onValueChange={setLookupType}>
              <SelectTrigger data-testid="lookup-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="local_id">local_id (número de la carta)</SelectItem>
                <SelectItem value="name_regex">nombre (ES o EN)</SelectItem>
                <SelectItem value="id_regex">id del mirror</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full flex-1">
            <Label className="text-xs text-neutral-400 mb-1 block">Valor</Label>
            <Input
              value={lookupValue}
              onChange={(e) => setLookupValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") runLookup(); }}
              placeholder={
                lookupType === "local_id" ? "ej. 211" :
                lookupType === "name_regex" ? "ej. mina" :
                "ej. swsh12"
              }
              data-testid="lookup-value-input"
            />
          </div>
          <Button
            onClick={runLookup}
            disabled={lookupLoading}
            data-testid="lookup-run-btn"
          >
            <BusyLabel busy={lookupLoading} text="Buscando…"><Search className="w-4 h-4 mr-2" />Buscar</BusyLabel>
          </Button>
        </div>

        {lookupData && (
          <div className="mt-4">
            {lookupData.length === 0 ? (
              <div className="text-sm text-neutral-400 italic" data-testid="lookup-empty">
                No se encontraron docs con ese filtro en el mirror.
              </div>
            ) : (
              <div className="space-y-3" data-testid="lookup-results">
                <div className="text-xs text-neutral-400">Se encontraron {lookupData.length} doc{lookupData.length > 1 ? "s" : ""}:</div>
                {lookupData.map((d, i) => {
                  const faltantes = d.palabras_del_nombre_faltantes_en_search_norm || [];
                  const ok = faltantes.length === 0;
                  return (
                    <div
                      key={i}
                      className={`border rounded-lg p-4 ${ok ? "border-emerald-800/40 bg-emerald-950/10" : "border-red-800/40 bg-red-950/10"}`}
                      data-testid={`lookup-doc-${i}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <div className="text-xs font-mono text-neutral-400">{d.id}</div>
                          <div className="text-lg font-display mt-1">{d.name_es || d.name_en || <span className="text-neutral-500 italic">sin nombre</span>}</div>
                        </div>
                        {ok ? (
                          <span className="text-xs bg-emerald-900/30 text-emerald-300 px-2 py-1 rounded flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> OK
                          </span>
                        ) : (
                          <span className="text-xs bg-red-900/30 text-red-300 px-2 py-1 rounded flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> INCONSISTENTE
                          </span>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-neutral-400">name_es:</span> <span className="text-blue-300">{d.name_es || <em className="text-neutral-500">vacío</em>}</span></div>
                        <div><span className="text-neutral-400">name_en:</span> <span className="text-amber-300">{d.name_en || <em className="text-neutral-500">vacío</em>}</span></div>
                        <div><span className="text-neutral-400">local_id:</span> {d.local_id || "—"}</div>
                        <div><span className="text-neutral-400">set:</span> {d.set_name || "—"} <span className="text-neutral-500">({d.set_id || "—"})</span></div>
                        <div><span className="text-neutral-400">search_prefix:</span> <code className="text-xs">{d.search_prefix || <em className="text-neutral-500">vacío</em>}</code></div>
                        <div><span className="text-neutral-400">trigrams:</span> {d.trigrams_count ?? 0}</div>
                      </div>

                      {!ok && (
                        <div className="mt-3 bg-red-950/30 border border-red-800/40 rounded p-3">
                          <div className="text-xs uppercase text-red-300 mb-1">Palabras del nombre real que NO están en search_norm:</div>
                          <div className="font-mono text-sm text-red-200">{faltantes.join(", ")}</div>
                        </div>
                      )}

                      <div className="mt-3 space-y-2">
                        <div>
                          <div className="text-xs uppercase text-neutral-500 mb-1">search_norm ACTUAL (lo guardado):</div>
                          <code className="block text-xs bg-[#050505] border border-[#222] rounded px-2 py-1.5 overflow-x-auto">{d.search_norm || <em className="text-neutral-500">vacío</em>}</code>
                        </div>
                        <div>
                          <div className="text-xs uppercase text-neutral-500 mb-1">search_norm ESPERADO (lo que el reindex escribiría):</div>
                          <code className="block text-xs bg-[#050505] border border-[#222] rounded px-2 py-1.5 overflow-x-auto text-emerald-300">{d.search_norm_esperado_ahora || <em className="text-neutral-500">vacío</em>}</code>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
